import React, { FC, ReactNode } from 'react'
import { Stack, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'

// Notion 流 inline banner. docs/design-system/03 §4.7.
// MUI <Alert> の重い bgcolor / icon を排し、 軽い 1 行帯で情報伝達.
type Severity = 'info' | 'warn' | 'danger' | 'success'

interface BannerProps {
  severity?: Severity
  icon?: ReactNode
  children: ReactNode
}

const SEVERITY_STYLES: Record<
  Severity,
  { bg: string; fg: string; iconColor: string; defaultIcon: ReactNode }
> = {
  info: {
    bg: colorTokens.state.infoBg,
    fg: colorTokens.state.infoFg,
    iconColor: colorTokens.state.infoFg,
    defaultIcon: <InfoOutlinedIcon sx={{ fontSize: 16 }} />,
  },
  warn: {
    bg: colorTokens.state.warnBg,
    fg: colorTokens.state.warnFg,
    iconColor: colorTokens.state.warnIcon,
    defaultIcon: <WarningAmberIcon sx={{ fontSize: 16 }} />,
  },
  danger: {
    bg: colorTokens.state.dangerBg,
    fg: colorTokens.state.dangerFg,
    iconColor: colorTokens.state.dangerIcon,
    defaultIcon: <ErrorOutlineIcon sx={{ fontSize: 16 }} />,
  },
  success: {
    bg: colorTokens.state.successBg,
    fg: colorTokens.state.successFg,
    iconColor: colorTokens.state.successFg,
    defaultIcon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />,
  },
}

export const Banner: FC<BannerProps> = ({
  severity = 'info',
  icon,
  children,
}) => {
  const style = SEVERITY_STYLES[severity]
  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      gap={1}
      sx={{
        py: 1,
        px: 1.5,
        bgcolor: style.bg,
        color: style.fg,
        borderRadius: `${RADIUS.md}px`,
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      <Stack sx={{ color: style.iconColor, mt: 0.25, flexShrink: 0 }}>
        {icon ?? style.defaultIcon}
      </Stack>
      <Typography
        sx={{
          fontSize: 13,
          color: style.fg,
          lineHeight: 1.5,
          flex: 1,
        }}
      >
        {children}
      </Typography>
    </Stack>
  )
}
